import React from 'react';
import { Trophy, Calendar, Users, Star, Lock, Tag, ChevronRight } from 'lucide-react';

interface TournamentCardPreviewProps {
  title: string;
  description: string;
  startDate?: string;
  endDate?: string;
  status?: 'upcoming' | 'active' | 'completed';
  prizeDescription?: string;
  prizeAmount?: number;
  priceCurrency?: string;
  participantCount?: number;
  maxParticipants?: number;
  isPrivate?: boolean;
  isFeatured?: boolean;
  categoryName?: string;
  categoryColor?: string;
  backgroundImageUrl?: string;
}

const DEFAULT_BG = 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80';

export const TournamentCardPreview: React.FC<TournamentCardPreviewProps> = ({
  title,
  description,
  startDate,
  endDate,
  status = 'upcoming',
  prizeDescription,
  prizeAmount,
  priceCurrency = '$',
  participantCount = 0,
  maxParticipants,
  isPrivate = false,
  isFeatured = false,
  categoryName,
  categoryColor,
  backgroundImageUrl
}) => {
  const bgImage = backgroundImageUrl || DEFAULT_BG;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const statusBadge = () => {
    if (status === 'active') return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-900/60 text-green-300">Active</span>;
    if (status === 'upcoming') return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-900/60 text-blue-300">Upcoming</span>;
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-900/60 text-gray-300">Completed</span>;
  };

  return (
    <div className="relative rounded-xl overflow-hidden cursor-pointer group" style={{ minHeight: 220 }}>
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
        style={{ backgroundImage: `url(${bgImage})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20" />

      <div className="relative z-10 p-5 flex flex-col justify-between h-full" style={{ minHeight: 220 }}>
        <div>
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center flex-wrap gap-1.5">
              {statusBadge()}
              {isPrivate && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-900/60 text-amber-300">
                  <Lock size={10} className="mr-1" />
                  Private
                </span>
              )}
              {isFeatured && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-yellow-900/60 text-yellow-300">
                  <Star size={10} className="mr-1" />
                  Featured
                </span>
              )}
              {categoryName && (
                <span
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold"
                  style={{
                    backgroundColor: categoryColor ? `${categoryColor}40` : 'rgba(255,255,255,0.15)',
                    color: categoryColor || 'white'
                  }}
                >
                  <Tag size={10} className="mr-1" />
                  {categoryName}
                </span>
              )}
            </div>
            <div className="bg-black/30 backdrop-blur-sm p-1.5 rounded-lg ml-2 flex-shrink-0">
              <Trophy size={16} className="text-yellow-400" />
            </div>
          </div>

          <h3 className="font-bold text-lg text-white leading-tight mb-1 drop-shadow">
            {title || 'Tournament Title'}
          </h3>
          <p className="text-white/75 text-sm line-clamp-2 drop-shadow-sm">
            {description || 'Tournament description appears here.'}
          </p>
        </div>

        <div className="mt-4 pt-3 border-t border-white/10">
          <div className="flex items-center justify-between text-xs text-white/70">
            <div className="flex items-center gap-3">
              {(startDate || endDate) && (
                <span className="flex items-center gap-1">
                  <Calendar size={11} />
                  {startDate && endDate
                    ? `${formatDate(startDate)} – ${formatDate(endDate)}`
                    : formatDate(startDate || endDate)}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Users size={11} />
                {participantCount}{maxParticipants ? ` / ${maxParticipants}` : ''}
              </span>
              {(prizeAmount || prizeDescription) && (
                <span className="flex items-center gap-1">
                  <Trophy size={11} />
                  {prizeAmount ? `${priceCurrency}${prizeAmount}` : prizeDescription?.substring(0, 20)}
                </span>
              )}
            </div>
            <span className="flex items-center gap-0.5 text-white/50">
              View <ChevronRight size={12} />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
